import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend } from "recharts";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";

// Styled components
const Wrapper = styled.div`
  width: 100%;
  padding: 20px;
`;

const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;

  &:hover {
    color: #000;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  margin-bottom: 20px;
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Select = styled.select`
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const SalesChangesByCountry = ({ salesData = [] }) => {
  const [filteredData, setFilteredData] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [hiddenCountries, setHiddenCountries] = useState(new Set());

  const monthNames = [
    "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
    "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
  ];

  useEffect(() => {
    if (salesData.length === 0) return;

    const years = [...new Set(salesData.map((row) => row.Rok.toString()))].sort();
    setAvailableYears(years);
    setSelectedYear(years.includes("2024") ? "2024" : years[0]);
  }, [salesData]);

  useEffect(() => {
    if (!selectedYear || salesData.length === 0) return;

    const monthlyData = Array.from({ length: 12 }, (_, i) => ({ month: monthNames[i] }));

    salesData
      .filter((row) => row.Rok.toString() === selectedYear)
      .forEach((row) => {
        const monthIndex = parseInt(row.Miesiac, 10) - 1;
        const country = row.KodKrajuNadaniaTIN?.trim() || "Unknown";
        const revenue =
          parseFloat(row.K_15) ||
          parseFloat(row.K_19) ||
          parseFloat(row.K_10) ||
          parseFloat(row.K_21) ||
          parseFloat(row.K_17) ||
          parseFloat(row.K_11) ||
          parseFloat(row.K_22) ||
          0;

        if (!monthlyData[monthIndex][country]) {
          monthlyData[monthIndex][country] = 0;
        }
        monthlyData[monthIndex][country] += revenue;
      });

    setFilteredData(monthlyData);
  }, [salesData, selectedYear]);

  const toggleCountry = (country) => {
    setHiddenCountries((prevHidden) => {
      const newHidden = new Set(prevHidden);
      if (newHidden.has(country)) {
        newHidden.delete(country);
      } else {
        newHidden.add(country);
      }
      return newHidden;
    });
  };

  return (
    <Wrapper>
<ChartTitle>
  Zmiany sprzedaży międzynarodowej w czasie
  <InfoIconWrapper
    data-tooltip-id="sales-tooltip"
    data-tooltip-content={
      "Zmiany sprzedaży\nLinia pokazuje wartość sprzedaży dla kraju\nw poszczególnych miesiącach wybranego roku."
    }
    data-tooltip-place="left"
  >
    <Info />
  </InfoIconWrapper>
</ChartTitle>
<InfoTooltip
  id="sales-tooltip"
  place="left"
  style={{ fontSize: "12px", fontFamily: "Arial, sans-serif", color: "white" }}
/>


      <Controls>
        <FilterGroup>
          <label>Rok:</label>
          <Select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
            {availableYears.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </Select>
        </FilterGroup>
      </Controls>

      <ResponsiveContainer width="100%" height={550}>
        <LineChart data={filteredData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis tickFormatter={(value) => value.toLocaleString("pl-PL")} />
          <Tooltip formatter={(value) => `${value.toLocaleString("pl-PL")} PLN`} />
          <Legend onClick={(e) => toggleCountry(e.value)} wrapperStyle={{ cursor: "pointer" }} />
          {filteredData.length > 0 &&
            Object.keys(filteredData[0]).filter((key) => key !== "month").map((country, index) => (
              <Line
                key={country}
                type="monotone"
                dataKey={country}
                stroke={`hsl(${index * 20}, 70%, 50%)`}
                strokeWidth={2}
                dot={{ r: 4 }}
                hide={hiddenCountries.has(country)}
              />
            ))}
        </LineChart>
      </ResponsiveContainer>
    </Wrapper>
  );
};

export default SalesChangesByCountry;
